import React, { useContext, useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import success from "../../Images/payment-success.png";
import form_success from "../../Images/payment-success.png";
import failed from "../../Images/payment-failed.png";
import pending from "../../Images/payment-pending.png";
import { MyContext } from "../../Context/MyProvider";
import ThemedPageFrame from "./ThemedPageFrame";

const PaymentStatus = () => {
    const { razorpay_order_id } = useParams();
    const { BaseUrl } = useContext(MyContext);

    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null); // success | pending | failed | form_success
    const [message, setMessage] = useState("");
    const [qrLink, setQrLink] = useState(null);
    const [retryUrl, setRetryUrl] = useState("/");

    const hasDownloadedRef = useRef(false);

    useEffect(() => {
        const savedReturn = localStorage.getItem("payment_form_return");
        if (savedReturn) {
            try {
                const parsed = JSON.parse(savedReturn);
                if (parsed?.slug) {
                    setRetryUrl(`/form/${parsed.slug}`);
                }
            } catch {
                setRetryUrl("/");
            }
        }
    }, []);

    useEffect(() => {
        if (razorpay_order_id === "1" || razorpay_order_id === 1) {
            setStatus("form_success");
            setMessage("Thank you! Your form has been submitted successfully.");

            const localQr = localStorage.getItem("qr_link");
            if (localQr) {
                setQrLink(localQr);
            }

            setLoading(false);
            return;
        }

        if (!razorpay_order_id) {
            setStatus("failed");
            setMessage("Invalid payment reference.");
            setLoading(false);
            return;
        }

        const verifyPayment = async () => {
            try {
                const res = await axios.post(
                    `${BaseUrl}user_api/verify_payment.php`,
                    { razorpay_order_id },
                    { headers: { "Content-Type": "application/json" } }
                );

                if (res?.data?.status === true && res?.data?.payment_status === "success") {
                    setStatus("success");
                    setMessage(res?.data?.message || "Your payment was successful.");

                    if (res?.data?.qr_link) {
                        setQrLink(res.data.qr_link);
                    }

                    // payment success => clear saved form
                    const savedReturn = localStorage.getItem("payment_form_return");
                    if (savedReturn) {
                        try {
                            const parsed = JSON.parse(savedReturn);
                            if (parsed?.slug) {
                                localStorage.removeItem(`form_draft_${parsed.slug}`);
                            }
                        } catch {}
                    }

                    localStorage.removeItem("payment_form_return");
                    localStorage.removeItem("payment_failed_order_id");
                } else if (res?.data?.status === false && res?.data?.payment_status === "pending") {
                    setStatus("pending");
                    setMessage(
                        res?.data?.message ||
                        "Your payment is pending. Please wait or check again later."
                    );
                } else {
                    setStatus("failed");
                    setMessage(res?.data?.message || "Payment verification failed.");
                }
            } catch (error) {
                setStatus("failed");
                setMessage("Unable to verify payment at the moment.");
                toast.error("Payment verification error");
            } finally {
                setLoading(false);
            }
        };

        verifyPayment();
    }, [BaseUrl, razorpay_order_id]);

    useEffect(() => {
        if (!qrLink || hasDownloadedRef.current) return;

        const link = document.createElement("a");
        link.href = qrLink;
        link.download = qrLink.split("/").pop() || "qr.png";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        hasDownloadedRef.current = true;
    }, [qrLink]);

    if (loading) {
        return (
            <ThemedPageFrame
                activePath="/"
                eyebrow="Payment"
                title="Verifying payment"
                subtitle="Please wait while we confirm your transaction."
                className="legacy_themed_page payment_status_page_layout"
            >
                <div className="payment_status_card text-lg font-semibold animate-pulse">
                    Verifying payment...
                </div>
            </ThemedPageFrame>
        );
    }

    const imageMap = {
        form_success,
        success,
        pending,
        failed,
    };

    const titleMap = {
        form_success: "Form Submitted Successfully",
        success: "Payment Successful",
        pending: "Payment Pending",
        failed: "Payment Failed",
    };

    const colorMap = {
        form_success: "text-green-600",
        success: "text-green-600",
        pending: "text-orange-500",
        failed: "text-red-600",
    };

    return (
        <ThemedPageFrame
            activePath="/"
            eyebrow="Payment"
            title={titleMap[status]}
            subtitle={message}
            className="legacy_themed_page payment_status_page_layout"
        >
            <div className="payment_status_card bg-[#515151] max-w-md w-full rounded-2xl shadow-lg p-8 text-center relative">
                {qrLink && (
                    <a
                        href={qrLink}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute top-2 right-0 inline-flex items-center justify-center px-4 py-2 text-blue-600 font-semibold hover:text-blue-800 transition"
                    >
                        Download QR
                    </a>
                )}

                <img
                    src={imageMap[status]}
                    alt="Payment Status"
                    className="w-28 mx-auto mb-6"
                />

                <h2 className={`text-2xl font-bold mb-2 text-white ${colorMap[status]}`}>
                    {titleMap[status]}
                </h2>

                <p className="text-white mb-6">
                    {message}
                </p>

                {status !== "form_success" && (
                    <div className="text-sm text-white mb-6 break-all">
                        <span className="font-semibold">Order ID:</span>{" "}
                        {razorpay_order_id}
                    </div>
                )}

                {(status === "success" || status === "form_success") ? (
                    <Link
                        to="/"
                        onClick={() => {
                            localStorage.removeItem("qr_link");
                        }}
                        className="inline-block px-6 py-3 rounded-full bg-green-600 text-white font-semibold hover:bg-green-700 transition"
                    >
                        Go to Home
                    </Link>
                ) : (
                    <Link
                        to={retryUrl}
                        className={`inline-block px-6 py-3 rounded-full ${
                            status === "pending" ? "bg-black" : "bg-red-600"
                        } text-white font-semibold ${
                            status === "pending" ? "hover:bg-orange-600" : "hover:bg-red-700"
                        } transition`}
                    >
                        Try Again
                    </Link>
                )}
            </div>
        </ThemedPageFrame>
    );
};

export default PaymentStatus;
